import { Layout } from "@/components/layout/Layout";
import { Helmet } from "react-helmet-async";

const PrivacyPolicy = () => {
  return (
    <Layout>
      <Helmet>
        <title>Privacy Policy | Taekwondo Mattei</title>
        <meta name="description" content="Informativa sulla privacy di Taekwondo Mattei ASD. Scopri come trattiamo i tuoi dati personali." />
        <link rel="canonical" href="https://taekwondomattei.com/privacy-policy" />
        <meta property="og:title" content="Privacy Policy | Taekwondo Mattei" />
        <meta property="og:description" content="Informativa sulla privacy di Taekwondo Mattei ASD." />
        <meta property="og:url" content="https://taekwondomattei.com/privacy-policy" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="it_IT" />
      </Helmet>

      <section className="pt-32 pb-20 bg-background">
        <div className="container-wide mx-auto px-4 md:px-8">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-8">
            Privacy Policy
          </h1>
          
          <div className="prose prose-lg max-w-none text-foreground/80">
            <p className="text-lg mb-6">
              <strong>Titolare del Trattamento:</strong> A.S.D. Taekwondo Mattei Roma<br />
              <strong>Codice Fiscale:</strong> 97831630583<br />
              <strong>Sede legale:</strong> Via Forno Saraceno, 129 – Roma<br />
              <strong>Email:</strong> taekwondomatteiroma@gmail.com<br />
              <strong>Ultimo aggiornamento:</strong> Febbraio 2026
            </p>

            <h2 className="font-display text-2xl font-bold text-foreground mt-10 mb-4">
              1. Introduzione
            </h2>
            <p>
              Taekwondo Mattei ASD (di seguito "l'Associazione") si impegna a proteggere la privacy dei propri utenti, atleti e visitatori del sito web. La presente informativa descrive le modalità di raccolta, utilizzo e protezione dei dati personali in conformità al Regolamento UE 2016/679 (GDPR) e al D.Lgs. 196/2003 come modificato dal D.Lgs. 101/2018.
            </p>

            <h2 className="font-display text-2xl font-bold text-foreground mt-10 mb-4">
              2. Dati Raccolti
            </h2>
            <p>L'Associazione può raccogliere le seguenti categorie di dati personali:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Dati identificativi:</strong> nome, cognome, data di nascita, codice fiscale</li>
              <li><strong>Dati di contatto:</strong> indirizzo email, numero di telefono, indirizzo di residenza</li>
              <li><strong>Dati relativi alla salute:</strong> certificati medici per l'idoneità sportiva (solo per gli atleti iscritti)</li>
              <li><strong>Dati di navigazione:</strong> indirizzo IP, tipo di browser, pagine visitate</li>
              <li><strong>Dati dei minori:</strong> per gli atleti minorenni, raccogliamo anche i dati del genitore/tutore</li>
            </ul>

            <h2 className="font-display text-2xl font-bold text-foreground mt-10 mb-4">
              3. Finalità del Trattamento
            </h2>
            <p>I dati personali vengono trattati per le seguenti finalità:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Gestione delle iscrizioni e dei tesseramenti sportivi</li>
              <li>Organizzazione delle attività sportive e degli eventi</li>
              <li>Comunicazioni relative ai corsi e alle attività dell'Associazione</li>
              <li>Adempimenti amministrativi e fiscali</li>
              <li>Risposta alle richieste di informazioni e prenotazioni lezioni di prova</li>
              <li>Invio di newsletter e comunicazioni promozionali (previo consenso)</li>
              <li>Miglioramento dei servizi offerti e analisi statistiche anonime</li>
            </ul>

            <h2 className="font-display text-2xl font-bold text-foreground mt-10 mb-4">
              4. Base Giuridica del Trattamento
            </h2>
            <p>Il trattamento dei dati personali si basa su:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Esecuzione del contratto:</strong> per la gestione dell'iscrizione e dei servizi richiesti</li>
              <li><strong>Obblighi legali:</strong> per adempimenti fiscali e normativi</li>
              <li><strong>Consenso:</strong> per l'invio di comunicazioni promozionali e newsletter</li>
              <li><strong>Legittimo interesse:</strong> per il miglioramento dei servizi e la sicurezza</li>
            </ul>

            <h2 className="font-display text-2xl font-bold text-foreground mt-10 mb-4">
              5. Conservazione dei Dati
            </h2>
            <p>
              I dati personali sono conservati per il tempo necessario al raggiungimento delle finalità per cui sono stati raccolti e comunque non oltre i termini previsti dalla legge. In particolare:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Dati degli atleti: per tutta la durata dell'iscrizione e per i 10 anni successivi</li>
              <li>Dati contabili e fiscali: 10 anni dalla data dell'ultima operazione</li>
              <li>Dati di navigazione: 24 mesi dalla raccolta</li>
              <li>Dati per marketing: fino alla revoca del consenso</li>
            </ul>

            <h2 className="font-display text-2xl font-bold text-foreground mt-10 mb-4">
              6. Condivisione dei Dati
            </h2>
            <p>I dati personali possono essere condivisi con:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Federazione Italiana Taekwondo (FITA) per i tesseramenti</li>
              <li>CONI e Enti di Promozione Sportiva</li>
              <li>Professionisti e consulenti (commercialisti, legali)</li>
              <li>Fornitori di servizi IT e hosting</li>
              <li>Autorità pubbliche quando richiesto dalla legge</li>
            </ul>

            <h2 className="font-display text-2xl font-bold text-foreground mt-10 mb-4">
              7. Diritti dell'Interessato
            </h2>
            <p>In qualità di interessato, hai il diritto di:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Accesso:</strong> ottenere conferma del trattamento e copia dei dati</li>
              <li><strong>Rettifica:</strong> correggere dati inesatti o incompleti</li>
              <li><strong>Cancellazione:</strong> richiedere la cancellazione dei dati (diritto all'oblio)</li>
              <li><strong>Limitazione:</strong> limitare il trattamento in determinate circostanze</li>
              <li><strong>Portabilità:</strong> ricevere i dati in formato strutturato</li>
              <li><strong>Opposizione:</strong> opporsi al trattamento per motivi legittimi</li>
              <li><strong>Revoca del consenso:</strong> revocare il consenso in qualsiasi momento</li>
            </ul>
            <p className="mt-4">
              Per esercitare i tuoi diritti, puoi contattarci all'indirizzo email: taekwondomatteiroma@gmail.com
            </p>

            <h2 className="font-display text-2xl font-bold text-foreground mt-10 mb-4">
              8. Sicurezza dei Dati
            </h2>
            <p>
              L'Associazione adotta misure tecniche e organizzative appropriate per proteggere i dati personali da accessi non autorizzati, perdita, distruzione o divulgazione. Queste misure includono la crittografia dei dati, l'accesso limitato ai dati e la formazione del personale.
            </p>

            <h2 className="font-display text-2xl font-bold text-foreground mt-10 mb-4">
              9. Reclami
            </h2>
            <p>
              Se ritieni che il trattamento dei tuoi dati personali violi il GDPR, hai il diritto di presentare un reclamo al Garante per la Protezione dei Dati Personali (www.garanteprivacy.it).
            </p>

            <h2 className="font-display text-2xl font-bold text-foreground mt-10 mb-4">
              10. Modifiche alla Privacy Policy
            </h2>
            <p>
              L'Associazione si riserva il diritto di modificare la presente informativa in qualsiasi momento. Le modifiche saranno pubblicate su questa pagina con indicazione della data di ultimo aggiornamento.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PrivacyPolicy;
